import { useFeature } from '@growthbook/growthbook-react';
import { last, omit } from 'ramda';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import {
  QUERY_KEY_COLLECTING_GAMES,
  QUERY_KEY_GAME,
  QUERY_KEY_GAMES,
  QUERY_KEY_GAMES_BY_IDS,
  QUERY_KEY_GAME_INFO,
  QUERY_KEY_INFINITY_GAMES,
  QUERY_KEY_WAGERING_GAMES,
} from '../../constants/api';
import FEATURES from '../../constants/featureToggling';
import {
  MIN_COUNT_CHARACTERS_FOR_SEARCH,
  SEARCH_QUERY_PARAM,
} from '../../constants/search';
import { isDebugModeSelector } from '../../redux/reducers/app/initialization';
import { getAPI } from '../../services/api';
import { sdkErrorHandler } from '../../utils';
import { useLobbyURL } from '../useLobbyURL';
import { useSearchParam } from '../useSearchParam';
import { DEFAULT_QUERY_PARAMS } from './constants';
import useRequestConfig from './useRequestConfig';
import { getStartQueryPage } from './utils';
import { useIsUnifiedPromoBonusSystemEnabled } from '../useIsUnifiedPromoBonusSystemEnabled';

export const useQueryCollectingGames = (campaignId, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    _start: 0,
    _limit: -1,
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
  };

  const fetch = ({ signal }) =>
    getAPI()
      .bonusGames.getCollectingGames(campaignId, {
        signal,
        query: queryParams,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          query: queryParams,
          queryOptions,
          campaignId,
          source: `SDK API bonusGames.getCollectingGames`,
        })
      );

  return useQuery(QUERY_KEY_COLLECTING_GAMES, fetch, queryOptions);
};

export const useQueryWageringGames = (queryOptions = {}, query = {}) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    _start: 0,
    _limit: -1,
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...query,
  };

  const fetch = ({ signal }) =>
    getAPI()
      .games.getAll({ signal, query: queryParams, ...requestConfig })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          query: queryParams,
          queryOptions,
          source: `SDK API games.getAll`,
        })
      );

  return useQuery([QUERY_KEY_WAGERING_GAMES, query], fetch, queryOptions);
};

export const useQueryGameInfo = (gameId, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isPromoSystemEnabled = useIsUnifiedPromoBonusSystemEnabled();

  const query = {
    ...(requestConfig?.query || {}),
    ...(isPromoSystemEnabled ? { isUnifiedLobby: true } : {}),
  };

  const fetch = ({ signal }) =>
    getAPI()
      .games.getInfo(gameId, {
        signal,
        apiVersion: 'v2',
        ...requestConfig,
        query,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          apiVersion: 'v2',
          gameId,
          queryOptions,
          query,
          source: `SDK API games.getInfo`,
        })
      );

  return useQuery([QUERY_KEY_GAME_INFO, gameId], fetch, queryOptions);
};

export const useQueryGame = (gameId, isDemo = false, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const lobbyUrl = useLobbyURL();

  const query = {};

  if (typeof lobbyUrl === 'string') {
    query.lobbyUrl = lobbyUrl;
  }

  const fetch = ({ signal }) =>
    isDemo
      ? getAPI()
          .games.getDemo(gameId, { signal, query, ...requestConfig })
          .catch(
            sdkErrorHandler({
              ...requestConfig,
              gameId,
              query,
              queryOptions,
              source: `SDK API games.getDemo`,
            })
          )
      : getAPI()
          .games.getReal(gameId, { signal, query, ...requestConfig })
          .catch(
            sdkErrorHandler({
              ...requestConfig,
              gameId,
              query,
              queryOptions,
              source: `SDK API games.getReal`,
            })
          );

  return useQuery([QUERY_KEY_GAME, gameId, isDemo], fetch, {
    staleTime: 0,
    cacheTime: 0,
    ...queryOptions,
    select: (data) => {
      const {
        url,
        backgroundImage,
        game,
        launchPayload,
        currencyConversion,
        limits,
      } = data;

      return {
        url,
        backgroundImage,
        game: {
          isDemo,
          ...game,
        },
        limits,
        currencyConversion,
        launchPayload,
      };
    },
  });
};

export const useQueryGames = (
  query = {},
  queryOptions = {},
  requestConfig = {}
) => {
  const defaultRequestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...DEFAULT_QUERY_PARAMS,
    ...query,
  };
  const fetch = ({ signal }) =>
    getAPI()
      .games.getAll({
        signal,
        query: queryParams,
        ...defaultRequestConfig,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...defaultRequestConfig,
          ...requestConfig,
          query: queryParams,
          queryOptions,
          source: `SDK API games.getAll`,
        })
      );

  return useQuery([QUERY_KEY_GAMES, queryParams], fetch, queryOptions);
};

export const useInfinityQueryGames = (query = {}, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...DEFAULT_QUERY_PARAMS,
    ...omit(['page'], query),
  };

  const fetch = ({ pageParam = getStartQueryPage(query), signal }) => {
    const fetchQueryParams = {
      ...queryParams,
      _start: pageParam * queryParams._limit,
    };
    return getAPI()
      .games.getAll({
        query: fetchQueryParams,
        signal,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          query: fetchQueryParams,
          queryOptions,
          source: `SDK API games.getAll`,
        })
      );
  };

  const infiniteQuery = useInfiniteQuery(
    [QUERY_KEY_INFINITY_GAMES, queryParams, query.page],
    fetch,
    {
      ...queryOptions,
      select: (data) => {
        const pagesInfo = last(
          data.pages.map((response) => omit(['items'], response))
        );

        return {
          pagesInfo,
          pages: [...data.pages.map((response) => response.items)].flat(),
          pageParams: data.pageParams,
        };
      },
      getNextPageParam: (pageData) => {
        return pageData.totalPages > pageData.currentPage
          ? pageData.currentPage
          : undefined;
      },
    }
  );

  const data = {
    pagesInfo: infiniteQuery?.data?.pagesInfo ?? {},
    pages: infiniteQuery?.data?.pages ?? [],
  };

  return { ...infiniteQuery, data };
};

export const useQuerySearchGames = (query = {}, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);
  const search = useSearchParam(SEARCH_QUERY_PARAM);
  const queryEnabled = search.length >= MIN_COUNT_CHARACTERS_FOR_SEARCH;

  const isNewSearchAPIEnabled = useFeature(FEATURES.USE_NEW_SEARCH_API).on;

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...DEFAULT_QUERY_PARAMS,
    _start: 0,
    _limit: -1,
    ...query,
  };

  const fetch = ({ signal }) =>
    isNewSearchAPIEnabled
      ? getAPI()
          .search.get({ signal, query: queryParams, ...requestConfig })
          .catch(
            sdkErrorHandler({
              ...requestConfig,
              query: queryParams,
              queryOptions,
              source: `SDK API search.get`,
            })
          )
      : getAPI()
          .games.getAll({ signal, query: queryParams, ...requestConfig })
          .catch(
            sdkErrorHandler({
              ...requestConfig,
              query: queryParams,
              queryOptions,
              source: `SDK API games.getAll`,
            })
          );

  return useQuery([QUERY_KEY_GAMES, queryParams], fetch, {
    enabled: queryEnabled,
    ...queryOptions,
    select: (data) => {
      return {
        ...data,
        items: data.items?.filter((game) =>
          game?.translationKey.toLowerCase().includes(search.toLowerCase())
        ),
      };
    },
  });
};

export const useQueryGamesByIds = (gameIds, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const limit = gameIds?.length || 0;
  const queryParams = { _start: 0, _limit: limit };

  const fetch = ({ signal }) =>
    getAPI()
      .games.getGamesByIds(gameIds, {
        query: queryParams,
        signal,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          query: queryParams,
          queryOptions,
          gameIds,
          source: `SDK API games.getGamesByIds`,
        })
      );

  return useQuery(
    [QUERY_KEY_GAMES_BY_IDS, gameIds, queryParams, limit],
    fetch,
    {
      ...queryOptions,
      enabled: Boolean(limit),
    }
  );
};
